import { extensions, view } from '@yola/ws-sdk';
import parseWidthToPx from '../../../common/helpers/parse-width-to-px';
import config from '../constants/config';
import {
  ASPECT_RATIO_CSS_PROPERTY,
  MAX_BUSINESS_LOGO_WIDTH,
  WIDTH_CSS_PROPERTY,
} from '../constants/media-container';

const getProbeWidth = (logo, settings) => {
  const styles = window.getComputedStyle(logo);
  // In legacy ws-header/ws-footer the logo's parent shrink-wraps the logo itself, so its width
  // equals the logo's current width. Probe against the block container instead.
  const parentWidth = Math.max(
    logo.closest(settings.blockSelector)?.clientWidth || 0,
    logo.parentElement?.clientWidth || 0
  );

  return Math.min(
    parseWidthToPx(styles.maxWidth, parentWidth) || parentWidth,
    MAX_BUSINESS_LOGO_WIDTH
  );
};

// Temporarily stretch the logo to its CSS max-width and measure the used width after
// flex/grid shrink. Aspect ratio matches the probe so accepted width keeps height stable
// for the same tick (restored before paint).
const measureAvailableWidth = (logo, settings) => {
  const { height } = logo.getBoundingClientRect();
  const probeWidth = getProbeWidth(logo, settings);

  if (!probeWidth || !height) {
    return null;
  }

  const previousStyle = logo.getAttribute('style');

  try {
    logo.style.setProperty(WIDTH_CSS_PROPERTY, `${probeWidth}px`);
    logo.style.setProperty(ASPECT_RATIO_CSS_PROPERTY, String(probeWidth / height));

    const { width: usedWidth } = logo.getBoundingClientRect();

    return usedWidth > 0 ? usedWidth : null;
  } finally {
    if (previousStyle === null) {
      logo.removeAttribute('style');
    } else {
      logo.setAttribute('style', previousStyle);
    }
  }
};

// All instances share one business logo global, so the width must fit the narrowest of them.
const getBusinessLogoMaxWidth = () => {
  const settings = extensions.accessors.getExtensionSettings(config.slug);
  const logos = view.accessors
    .getLiveDocument()
    .querySelectorAll(`${settings.businessLogoSelector} ${settings.imageContainer}`);

  const availableWidths = [...logos]
    .filter((logo) => logo.getClientRects().length)
    .map((logo) => measureAvailableWidth(logo, settings))
    .filter((width) => Number.isFinite(width) && width > 0);

  if (!availableWidths.length) {
    return null;
  }

  return Math.min(...availableWidths);
};

export default getBusinessLogoMaxWidth;
